<!-- filepath: c:\Users\lawi\Sandbox\loop_reporting_feb\frontend\src\componentsv3\KpiSearchBar.vue -->
<template>
  <div class="kpi-search-wrapper">
    <div class="kpi-search-container" :class="{ 'is-disabled': isDisabled }">
      <Search :size="20" class="search-icon" />
      <input  v-model="internalValue"
              type="search"
              class="kpi-search-input"
              :placeholder="placeholder"
              :disabled="isDisabled"
              @input="handleInput"
              @focus="handleFocus"
              @blur="handleBlur"
              @keydown.enter="handleEnter"
              @keydown.esc="handleEscape"
              @keydown.down.prevent="navigateDown"
              @keydown.up.prevent="navigateUp" />

      <!-- Status wenn Dropdown geschlossen -->
      <span v-if="!isDisabled && !showResults && !internalValue" class="status-text">
        {{ items.length }} KPIs
      </span>

      <!-- Status wenn gefiltert wird (Dropdown offen) -->
      <span v-if="!isDisabled && showResults && items.length > 0" class="status-text">
        <strong>{{ filteredItems.length }}</strong> / {{ items.length }}
      </span>

      <!-- OK Icon wenn Option selektiert -->
      <span v-if="!isDisabled && !showResults && internalValue && hasSelection" class="status-icon">
        <Check :size="18" />
      </span>

      <button v-if="internalValue && !isDisabled"
              class="clear-button"
              type="button"
              @click="clear"
              aria-label="Clear search">
        <X :size="18" />
      </button>
    </div>

    <!-- Dropdown Results -->
    <div v-if="showResults && displayItems.length > 0" class="search-results">
      <div  v-for="(item, index) in displayItems"
            :key="index"
            class="search-result-item"
            :class="{ 'is-active': index === activeIndex }"
            @mousedown.prevent="selectItem(item)"
            @mouseenter="activeIndex = index">

        <!-- ✅ Slot für Custom Template -->
        <slot name="item"
              :item="item"
              :highlighted="highlightedItems[index]"
              :index="index"
              :is-active="index === activeIndex">
          <!-- Default Template mit Icon und Badge -->
          <div class="default-item-layout">
            <Group v-if="item.type === 'group'" :size="16" class="item-icon" />
            <ChartColumnIncreasing v-else-if="item.type === 'kpi'" :size="16" class="item-icon" />

            <span v-html="highlightedItems[index]" class="item-label"></span>

            <span class="item-badge">
              {{ item.type === 'group' ? 'Group' : 'KPI' }}
            </span>
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import { Search, X, Check, ChartColumnIncreasing, Group } from 'lucide-vue-next';

export default {
  name: 'KpiSearchBar',

  components: {
    Search,
    X,
    Check,
    ChartColumnIncreasing,
    Group
  },

  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Suchen...'
    },
    items: {
      type: Array,
      default: () => []
    },
    itemLabel: {
      type: String,
      default: null
    },
    minLength: {
      type: Number,
      default: 0
    },
    maxItems: {
      type: Number,
      default: 50
    }
  },

  emits: ['update:modelValue', 'select'],

  data() {
    return {
      internalValue: this.modelValue,
      showResults: false,
      activeIndex: -1,
      blurTimeout: null,
      cachedRegex: null,
      cachedQuery: '',
      hasSelection: false
    }
  },

  computed: {
    isDisabled() {
      return !this.items || this.items.length === 0;
    },

    filteredItems() {
      if (!this.internalValue || this.internalValue.length < this.minLength) {
        return this.items;
      }

      const query = this.cachedQuery;
      return this.items.filter(item => {
        const searchText = this.getItemLabel(item);
        return searchText.toLowerCase().includes(query);
      });
    },

    displayItems() {
      return this.filteredItems.slice(0, this.maxItems);
    },

    highlightedItems() {
      if (!this.internalValue || !this.cachedRegex) {
        return this.displayItems.map(item => this.getItemLabel(item));
      }

      const regex = this.cachedRegex;
      const replacement = '<strong style="text-decoration: underline;">$1</strong>';

      return this.displayItems.map(item => {
        const label = this.getItemLabel(item);
        return label.replace(regex, replacement);
      });
    }
  },

  watch: {
    modelValue(newVal) {
      this.internalValue = newVal;
      // Wenn modelValue von außen auf leer gesetzt wird, reset hasSelection
      if (!newVal) {
        this.hasSelection = false;
      }
    }
  },

  methods: {
    getItemLabel(item) {
      if (typeof item === 'string') {
        return item;
      }

      if (this.itemLabel && item[this.itemLabel]) {
        return item[this.itemLabel];
      }

      return item.label || item.name || item.text || String(item);
    },

    handleInput(event) {
      this.internalValue = event.target.value;

      // Bei manueller Eingabe: hasSelection auf false setzen
      this.hasSelection = false;

      this.cachedQuery = this.internalValue.toLowerCase();
      this.cachedRegex = this.internalValue
        ? new RegExp(`(${this.escapeRegex(this.internalValue)})`, 'gi')
        : null;

      this.$emit('update:modelValue', this.internalValue);
      this.showResults = true;
      this.activeIndex = -1;
    },

    handleFocus() {
      if (!this.isDisabled) {
        this.showResults = true;
        this.activeIndex = -1;

        if (this.blurTimeout) {
          clearTimeout(this.blurTimeout);
          this.blurTimeout = null;
        }
      }
    },

    handleBlur() {
      if (this.blurTimeout) {
        clearTimeout(this.blurTimeout);
      }

      this.blurTimeout = setTimeout(() => {
        this.showResults = false;
        this.activeIndex = -1;
        this.blurTimeout = null;
      }, 200);
    },

    navigateDown() {
      if (this.activeIndex < this.displayItems.length - 1) {
        this.activeIndex++;
      }
    },

    navigateUp() {
      if (this.activeIndex > 0) {
        this.activeIndex--;
      }
    },

    handleEnter() {
      if (this.activeIndex >= 0 && this.displayItems.length > 0) {
        this.selectItem(this.displayItems[this.activeIndex]);
      } else if (this.displayItems.length > 0) {
        this.selectItem(this.displayItems[0]);
      }
    },

    handleEscape() {
      this.showResults = false;
      this.activeIndex = -1;
    },

    selectItem(item) {
      const label = this.getItemLabel(item);
      this.internalValue = label;
      this.hasSelection = true; // ✅ Item wurde ausgewählt
      this.$emit('update:modelValue', label);
      this.$emit('select', item);
      this.showResults = false;
      this.activeIndex = -1;
    },

    clear() {
      this.internalValue = '';
      this.cachedQuery = '';
      this.cachedRegex = null;
      this.hasSelection = false; // ✅ Reset bei Clear
      this.$emit('update:modelValue', '');
      this.$emit('select', undefined);
      this.showResults = false;
      this.activeIndex = -1;
    },

    escapeRegex(string) {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
  },

  beforeUnmount() {
    if (this.blurTimeout) {
      clearTimeout(this.blurTimeout);
    }
  }
}
</script>

<style lang="scss" scoped>
.kpi-search-wrapper {
  position: relative;
  width: 100%;
}

.kpi-search-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: var(--radius-md);
  // transition: border-color 0.15s ease-in-out;

  &:focus-within {
    border-color: var(--bs-border-color);
    box-shadow: none;
  }

  &.is-disabled {
    background-color: var(--bs-secondary-bg);
    cursor: not-allowed;
    opacity: 0.65;

    .search-icon,
    .kpi-search-input {
      cursor: not-allowed;
    }
  }
}

.search-icon {
  position: absolute;
  left: 12px;
  color: var(--bs-secondary-color);
  pointer-events: none;
}

.kpi-search-input {
  flex: 1;
  padding: 0.5rem 2.5rem;
  border: none;
  background: transparent;
  color: var(--bs-body-color);
  font-size: 1rem;
  outline: none;

  &::placeholder {
    color: var(--bs-secondary-color);
  }

  &::-webkit-search-cancel-button {
    display: none;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.status-text {
  position: absolute;
  right: 40px;
  font-size: 0.875rem;
  color: var(--bs-secondary-color);
  pointer-events: none;
  white-space: nowrap;

  strong {
    font-weight: 600;
    color: var(--bs-body-color);
  }
}

.status-icon {
  position: absolute;
  right: 40px;
  display: flex;
  align-items: center;
  color: var(--bs-success);
  pointer-events: none;
}

.clear-button {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  background: transparent;
  border: none;
  color: var(--bs-secondary-color);
  cursor: pointer;
  border-radius: var(--radius-sm);
  //transition: all 0.15s ease-in-out;

  &:hover {
    color: var(--bs-body-color);
    background-color: var(--bs-secondary-bg);
  }

  &:active {
    transform: scale(0.95);
  }
}

.search-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: var(--radius-md);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  z-index: 1000;
}

.search-result-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  color: var(--bs-body-color);
  // transition: background-color 0.15s ease-in-out;

  &:hover,
  &.is-active {
    background-color: var(--bs-secondary-bg);
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--bs-border-color);
  }

  :deep(strong) {
    color: var(--bs-primary);
    font-weight: 600;
    text-decoration: underline;
  }
}

// Default Item Layout
.default-item-layout {
  display: flex;
  align-items: center;
  gap: 8px;

  .item-icon {
    flex-shrink: 0;
    color: var(--bs-secondary-color);
  }

  .item-label {
    flex: 1;
    min-width: 0; // Ermöglicht text-overflow
  }

  .item-badge {
    flex-shrink: 0;
    margin-left: auto;
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    background-color: var(--bs-secondary-bg);
    color: var(--bs-secondary-color);
  }
}
</style>
